import { useEffect, useMemo, useRef, useState } from "react";

import { acqService, type AcqProduct } from "../api/acqService";

/**
 * 本租户在售产品(`GET /tenant/products`)—— 获客舱四个页面共用的那份「我卖什么」。
 *
 * ★ 为什么要一个 hook 而不是各页各拉一次:目录页要拿它把关键词分组的**组标题**翻成产品名,
 *   今日商机与线索库要拿它做筛子,「我的生意」第一栏就是它。四处各抄一遍的后果是
 *   "同一个 node_code 在两页上显示成两个名字"(一处用了 code、一处用了 name)。
 *
 * ★★ `nameOf(code)` 取不到时**回落到 code 本身,不回落到「未知产品」**:
 *   产品下架 / 改码之后,面上露出 `tm_madrid` 这种码是能查的线索;写「未知产品」
 *   把线索抹掉了,而用户看到的两条都长一样(与 `sourceLabel` / `archetypeLabel` 同则)。
 *
 * ★ 拉不到不报错、不挡页面:产品树是**大多数租户都没建**的(实测 33 个租户 33 家产品树空)。
 *   那时筛子里只有「全部产品」、分组标题全落「通用」—— 页面照常用,只是少一层分类。
 */
export interface TenantProducts {
  products: AcqProduct[];
  loading: boolean;
  nameOf: (code: string | null | undefined) => string;
}

export function useTenantProducts(): TenantProducts {
  const [products, setProducts] = useState<AcqProduct[]>([]);
  const [loading, setLoading] = useState(true);
  /** StrictMode 下 effect 跑两遍;这里只是多一次 GET,但 setState 两次会让筛子闪一下。 */
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // ★★ 这里**故意没有 `alive` 取消旗**(真渲染抓到的:组标题全是 `ip_hosting` 这种码)。
    //   StrictMode 下 effect 会 mount → unmount → mount:`started` 拦掉了第二次请求,
    //   而第一次请求的结果正好被第一次 cleanup 的 `alive=false` 丢掉 ⇒ **产品名永远拿不到**,
    //   页面上于是露出 node_code。两个各自正确的写法(去重 + 取消)合在一起把数据吃了。
    //   React 18 起在已卸载组件上 setState 是无害的空操作,所以这里直接写回。
    void acqService.tenantProducts().then(
      (list) => {
        setProducts(list);
        setLoading(false);
      },
      () => {
        // 产品树读不到就是"没有产品可筛" —— 不弹错、不挡页面。
        setLoading(false);
      }
    );
  }, []);

  const byCode = useMemo(() => new Map(products.map((p) => [p.nodeCode, p.name])), [products]);

  return useMemo(
    () => ({
      products,
      loading,
      nameOf: (code) => (code ? (byCode.get(code) ?? code) : ""),
    }),
    [products, loading, byCode]
  );
}
